import { motion } from 'framer-motion';
import { 
  WrenchScrewdriverIcon,
  BeakerIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  CommandLineIcon
} from '@heroicons/react/24/outline';

const Services = () => {
  const services = [
    {
      title: "Build",
      description: "We design and develop scalable software—fast, secure, and built for your users.",
      icon: WrenchScrewdriverIcon,
      code: "SRV_001"
    },
    {
      title: "Testing",
      description: "We run deep Blockchain QA across automation and manual flows to catch issues before your users do.",
      icon: BeakerIcon,
      code: "SRV_002"
    },
    {
      title: "Audit",
      description: "We audit smart contracts and systems for security and performance during different phases of development lifecycle.",
      icon: ShieldCheckIcon,
      code: "SRV_003"
    },
    {
      title: "Deployment & Support",
      description: "We ship, monitor, and maintain your software so it runs smoothly—day one and beyond.",
      icon: RocketLaunchIcon,
      code: "SRV_004"
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:14px_24px]" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative"
      >
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
          [SYSTEM::SERVICES]
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-6 font-mono">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
              How We Deliver Value
            </span>
          </h2>
        </div>

        <div className="space-y-12">
          {/* Services Section */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg backdrop-blur-sm border border-pink-500/20 
              group-hover:border-pink-500/30 transition-all duration-300" />
            
            <div className="relative p-8">
              <h3 className="text-xl font-bold text-white mb-8 font-mono">Services - Built for Next Era</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {services.map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                      group-hover:border-indigo-500/40 transition-all duration-300" />
                    
                    <div className="relative p-6">
                      <div className="flex items-center justify-between mb-4">
                        <service.icon className="w-8 h-8 text-indigo-400" />
                        <div className="font-mono text-xs text-indigo-500/60">{service.code}</div>
                      </div>
                      
                      <h4 className="text-xl font-bold text-white mb-3 font-mono">{service.title}</h4>
                      <p className="text-slate-400 font-mono">{service.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Products Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg backdrop-blur-sm border border-pink-500/20 
              group-hover:border-pink-500/30 transition-all duration-300" />
            
            <div className="relative p-8">
              <div className="flex items-center justify-between mb-6">
                <UserGroupIcon className="w-8 h-8 text-indigo-400" />
                <div className="font-mono text-xs text-indigo-500/60">PRD_001</div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3 font-mono">Products - Web3 & AI made User-Friendly</h3>
              <p className="text-slate-400 font-mono mb-2">Onboarding the next generation.</p>
              <p className="text-slate-400 font-mono">We design intuitive tools and products that bridge users into Web3 and AI</p>
            </div>
          </motion.div>

          {/* zkMonk Community Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg backdrop-blur-sm border border-pink-500/20 
              group-hover:border-pink-500/30 transition-all duration-300" />
            
            <div className="relative p-8">
              <div className="flex items-center justify-between mb-6">
                <CommandLineIcon className="w-8 h-8 text-indigo-400" />
                <div className="font-mono text-xs text-indigo-500/60">COM_002</div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3 font-mono">zkMonk - Our Community</h3>
              <p className="text-slate-400 font-mono">We're more than a team—we're a ZK engineering community driving innovation in privacy-preserving tech.</p>
            </div>
          </motion.div>
        </div>

        {/* Footer decoration */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
          [END::SERVICES]
        </div>
      </motion.div>
    </section>
  );
};

export default Services;